import { ACTOR_ID_HEADER_KEY, O_CLIENT_APP, PAYMENT_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const CHARGES_BASE = `${PAYMENT_SERVICE_BASE_URL}/api/v1/payments/charges`;
const PAYMENTS_BASE = `${PAYMENT_SERVICE_BASE_URL}/api/v1/payments`;
const CHARGE_CONFIG_BASE = `${PAYMENT_SERVICE_BASE_URL}/api/v1/charges`;

const actorHeaders = (actorId) =>
  actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {};

const withPaging = (params = {}) => {
  const { page = 0, size = 20, ...rest } = params;
  return { page, size, sort: "createdAt,desc", ...rest };
};

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const chargePayerWorkspaceId = (charge) =>
  charge?.payerWorkspaceId ||
  charge?.payerWorkspace?.id ||
  charge?.paymentChargeDetails?.payerWorkspaceId ||
  charge?.paymentChargeDetails?.payerWorkspace?.id ||
  charge?.chargeDetails?.payerWorkspaceId ||
  charge?.chargeDetails?.payerWorkspace?.id ||
  null;

const withScopedContent = (result, { payerWorkspaceId } = {}) => {
  const content = pageContent(result).filter((charge) => {
    if (!payerWorkspaceId) return !chargePayerWorkspaceId(charge);
    const scopedWorkspaceId = chargePayerWorkspaceId(charge);
    return !scopedWorkspaceId || scopedWorkspaceId === payerWorkspaceId;
  });

  if (Array.isArray(result)) return content;
  return { ...result, content };
};

export const paymentChargesService = {
  outgoing: {
    listForUser: async ({ actorId, userId, workspaceId, payerWorkspaceId, start, end, page = 0, size = 20 } = {}) => {
      const scopedPayerWorkspaceId = payerWorkspaceId || workspaceId;
      const response = await apiClient.get(`${CHARGES_BASE}/user/range`, {
        params: withPaging({
          uid: userId || undefined,
          payerWorkspaceId: scopedPayerWorkspaceId || undefined,
          start,
          end,
          page,
          size,
        }),
        headers: actorHeaders(actorId),
      });
      return withScopedContent(response.data || { content: [] }, { payerWorkspaceId: scopedPayerWorkspaceId });
    },
  },

  receivable: {
    listForOrganization: async ({
      actorId,
      userId,
      organizationId,
      workspaceId,
      status,
      start,
      end,
      page = 0,
      size = 20,
    } = {}) => {
      const response = await apiClient.get(`${CHARGES_BASE}/receivable/user/range`, {
        params: withPaging({
          uid: userId || actorId || undefined,
          payeeWorkspaceId: workspaceId || undefined,
          status: status || undefined,
          start,
          end,
          page,
          size,
        }),
        headers: actorHeaders(actorId),
      });
      return response.data || { content: [] };
    },
  },

  earnings: {
    listAvailableCharges: async ({ actorId, payeeId, payeeWorkspaceId, status = "SUCCESSFUL" } = {}) => {
      if (!payeeWorkspaceId) {
        throw new Error("payeeWorkspaceId is required to load available earnings.");
      }
      const response = await apiClient.get(`${PAYMENTS_BASE}/earnings/charges`, {
        params: {
          status,
          payeeWorkspaceId,
          payeeId: payeeId || undefined,
        },
        headers: actorHeaders(actorId),
      });
      return Array.isArray(response.data) ? response.data : [];
    },

    cashOut: async ({
      actorId,
      chargeIds,
      payeeId,
      payeeWorkspaceId,
      currency = "USD",
      totalAmount,
      totalCharges,
      netPayout,
      charges = [],
    } = {}) => {
      if (!payeeWorkspaceId) {
        throw new Error("payeeWorkspaceId is required to cash out earnings.");
      }
      const response = await apiClient.post(
        `${PAYMENTS_BASE}/earnings/cashout`,
        {
          chargeIds,
          payeeWorkspaceId,
          ...(payeeId ? { payeeId } : {}),
          currency,
          totalAmount,
          totalCharges,
          netPayout,
          charges,
        },
        {
          params: {
            payeeWorkspaceId,
            payeeId: payeeId || undefined,
          },
          headers: actorHeaders(actorId),
        },
      );
      return response.data || {};
    },
  },

  payouts: {
    listReceived: async ({ actorId, userId, payeeWorkspaceId } = {}) => {
      if (!payeeWorkspaceId) {
        throw new Error("payeeWorkspaceId is required to load payout activities.");
      }
      const response = await apiClient.get(
        `${PAYMENTS_BASE}/payout/user/${encodeURIComponent(userId || actorId)}/received`,
        {
          params: { payeeWorkspaceId },
          headers: actorHeaders(actorId),
        },
      );
      const content = pageContent(response.data);
      return content.filter((item) => {
        const recipientWorkspaceId = item?.recipientWorkspaceId || item?.recipientWorkspace?.id;
        return String(recipientWorkspaceId || "") === String(payeeWorkspaceId);
      });
    },

    getDefaultExternalAccount: async ({ actorId, workspaceId } = {}) => {
      const response = await apiClient.get(`${PAYMENTS_BASE}/external-account/default`, {
        params: { workspaceId: workspaceId || undefined },
        headers: actorHeaders(actorId),
      });
      return response.data || null;
    },

    listEffectiveCharges: async ({ actorId, amount, currency = "USD" } = {}) => {
      if (!amount || Number(amount) <= 0) return [];
      const response = await apiClient.get(`${CHARGE_CONFIG_BASE}/effective/paged`, {
        params: {
          app: O_CLIENT_APP,
          currency,
          stages: `${O_CLIENT_APP}_ALL`,
          chargeTarget: "PAYOUT",
          amount: Number(amount || 0).toFixed(2),
          page: 0,
          size: 20,
        },
        headers: actorHeaders(actorId),
      });
      return pageContent(response.data);
    },

    createDashboardLink: async ({ actorId, userId, workspaceId } = {}) => {
      const response = await apiClient.post(
        `${PAYMENTS_BASE}/stripe-connect/helper/${encodeURIComponent(userId || actorId)}/dashboard`,
        null,
        {
          params: { workspaceId: workspaceId || undefined },
          headers: actorHeaders(actorId),
        },
      );
      return response.data || {};
    },
  },
};

export default paymentChargesService;
